import axios from "@/plugins/axios"

export const fileUpload = async (file: File | string, type: string, id?: number): Promise<any> => {
	const payload = {
		file: file,
		type: type,
		record_id: id
	}

	const response = await axios.post(`api/v1/files/upload`, payload, {
		headers: { "Content-Type": "multipart/form-data" }
	})

	return response
}

export const fetchCompanyListRequest = async (page: number, bookable = false): Promise<any> => {
	// bookable=1 -> backend returns only Active children (the ones an agency can
	// actually book/upload for). The Companies tab omits it to see every child.
	const params: Record<string, any> = { page, per_page: 20 }
	if (bookable) params.bookable = 1
	const r = await axios.get("/api/v1/companies/children", { params })
	const items: any[] = r.data?.data ?? []
	const meta = r.data?.meta?.pagination ?? r.data?.meta ?? {}
	return {
		data: items.map((it) => {
			const a = it.attributes ?? {}
			const d = a.detail ?? {}
			return {
				id:           it.id,
				company_name: d.company_name ?? a.company_name ?? "",
				address:      d.address ?? "",
				email:        a.email ?? "",
				phone:        d.phone ?? "",
				created_at:   a.created_at ?? "",
			}
		}),
		current_page: meta.current_page ?? page,
		total:        meta.total ?? items.length,
		per_page:     meta.per_page ?? 20,
	}
}

export const fetchCategoriesListRequest = async (): Promise<any> => {
	const r = await axios.get(`/api/v1/catalog/place-categories`)
	const list: any[] = r.data?.data ?? []
	// Flatten JSON:API { id, attributes:{ name, slug, ... } } so the dropdown
	// can read `name` / `slug` directly.
	return list.map((n) => (n?.attributes ? { id: n.id, ...n.attributes } : n))
}

// Legacy v1 reused /api/category for orientations; v2 doesn't expose them
// as a separate endpoint (Screen.orientation is an enum). Returning an
// empty list keeps callers happy without surfacing a 404.
export const fetchOrientationListRequest = async (): Promise<any> => {
	return []
}

export const fetchLocationListRequest = async (params: {
	page?: number;
	per_page?: number;
	status?: string;
}): Promise<{
	data: { id: string; name: string }[];
	current_page: number;
	per_page: number;
	total: number;
	last_page: number;
}> => {
	const r = await axios.get("/api/v1/places", {
		params: {
			page: params.page ?? 1,
			per_page: params.per_page ?? 30,
			status: params.status ?? "active",
		},
	})
	const rows: any[] = r.data?.data ?? []
	const meta = r.data?.meta?.pagination ?? {}
	return {
		data: rows.map((p) => ({
			id: String(p.id),
			name: String(p.attributes?.name ?? p.name ?? p.id),
		})),
		current_page: Number(meta.current_page ?? 1),
		per_page:     Number(meta.per_page ?? rows.length),
		total:        Number(meta.total ?? rows.length),
		last_page:    Number(meta.last_page ?? 1),
	}
}

export const searchLocationRequest = async (params: {
	search_text: string;
	status?: string;
}): Promise<{ data: { id: string; name: string }[] }> => {
	const r = await axios.get("/api/v1/places", {
		params: {
			search: params.search_text,
			status: params.status ?? "active",
			per_page: 30,
		},
	})
	const rows: any[] = r.data?.data ?? []
	return {
		data: rows.map((p) => ({
			id: String(p.id),
			name: String(p.attributes?.name ?? p.name ?? p.id),
		})),
	}
}

/**
 * v2 email confirmation: POST /auth/confirm-registration with { token }.
 * Returns the user envelope (no tokens — verified user has to sign in
 * normally). Backend rejects expired/used/missing tokens with 422.
 */
export const confirmRegistrationRequest = async (token: string): Promise<any> => {
	const r = await axios.post("/api/v1/auth/confirm-registration", { token })
	const d = r.data?.data
	if (!d) return null
	const a = d.attributes ?? {}
	return { id: d.id, ...a, user_detail: a.detail ?? null }
}

export const createStripeLinkRequest = async (): Promise<string | null> => {
	try {
		const r = await axios.get(`/api/v1/payment/account-link`)
		return r.data?.data?.attributes?.url ?? null
	} catch (error) {
		console.error(error)
		return null
	}
}

export const fetchNotifications = async () => {
	try {
		const r = await axios.get(`api/v1/notifications`)
		const rows: any[] = r.data?.data ?? []
		// Flatten JSON:API { id, type, attributes } to the flat shape the
		// notification list/header dropdown consume (`title`, `description`,
		// `type`, `read_at`). v2 calls the body field `body` — rename it to
		// `description` so the existing NotificationItem template keeps
		// rendering without changes.
		return {
			data: rows.map((n) => {
				const a = n.attributes ?? {}
				return {
					id:             n.id,
					type:           a.event_key ?? a.reference_type ?? "info",
					title:          a.title ?? "",
					description:    a.body ?? "",
					reference_type: a.reference_type ?? null,
					reference_id:   a.reference_id ?? null,
					read_at:        a.read_at ?? null,
					created_at:     a.created_at ?? null,
				}
			}),
		}
	} catch (error) {
		console.error(error)
		return { data: [] }
	}
}
export const fetchUnreadNotificationsCount = async (): Promise<{ count: number }> => {
	try {
		const r = await axios.get(`api/v1/notifications/unread-count`)
		return { count: r.data?.data?.attributes?.count ?? 0 }
	} catch (error) {
		console.error(error)
		return { count: 0 }
	}
}
export const markNotificationAsRead = async (id: string) => {
	try {
		const r = await axios.post(`api/v1/notifications/${id}/read`)
		return r.data?.data ?? null
	} catch (error) {
		console.error(error)
		return null
	}
}

export const markAllNotificationsAsRead = async () => {
	try {
		const r = await axios.post(`api/v1/notifications/mark-all-read`)
		return r.data?.data ?? null
	} catch (error) {
		console.error(error)
		return null
	}
}
